import type { NotificationPayloadT } from 'lib-shared-legacy-types';
import { connect } from 'react-redux';
import { Action, Dispatch } from 'redux';
import { createSelector } from 'reselect';

import { RootReducerStateT } from 'app/reducers/rootReducer';
import { FlowEmailStateT } from 'components/powerEmailFlow/reducers/emailFlow';
import {
  FiltersNodeRuleMatch,
  FiltersNodeRuleVariable,
  FlowNodeFilters,
} from 'components/powerFlowShared/components/nodeTypes/filters';
import {
  FlowNodeTypes,
  MatchingOperator,
  MatchingStrategy,
} from 'components/powerFlowShared/components/types';
import type { NodeDataT } from 'fable/flowEditor';
import { withRouter } from 'hocs/router';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import { notificationActions } from '../../notifications/actions/notification';
import { emailFlowActions } from '../actions/emailFlowActions';
import { FlowEmailActionT } from '../api/types';
import PowerEmailFlow, {
  FlowEmailOwnPropsT,
} from '../components/PowerEmailFlow';
import {
  ContentRule,
  FlowEmailNodeContentT,
} from '../components/nodeTypes/content';

export interface FlowDataT extends NodeDataT<FlowEmailActionT[]> {
  actions: FlowEmailActionT[];
  active: boolean;
  created_at: string;
  id: string;
  name: string;
  updated_at: string;
}

const { notificationAdd } = notificationActions;
const { emailFlowGet, emailFlowEdit, emailFlowResetState } = emailFlowActions;

const getId = (_state: RootReducerStateT, ownProps: FlowEmailOwnPropsT) => {
  return ownProps.params.flowId;
};

const contentRuleTypeToRuleVariableT = (
  type: ContentRule['type'],
): FiltersNodeRuleVariable => {
  return {
    type: 'system',
    id: type === 'from' ? 'from_email' : type,
  };
};
const contentRuleRuleToMatchingOperator = (
  rule: ContentRule['rule'],
): MatchingOperator => {
  return rule as MatchingOperator;
};
const contentRuleMatchToMatchingRule = (
  match: ContentRule['match'],
): FiltersNodeRuleMatch => {
  return {
    id: '',
    type: 'input',
    value: match,
  };
};

const convertContentToBranchNode = (
  node: FlowEmailNodeContentT,
): FlowNodeFilters => {
  const { payload } = node;
  return {
    ...node,
    nodeType: FlowNodeTypes.FILTERS,
    payload: {
      match_node_id: payload.match_node_id,
      non_match_node_id: payload.non_match_node_id,
      matching_strategy: payload.rule_type as MatchingStrategy,
      rules: payload.rules.map((rule) => {
        return {
          variable: contentRuleTypeToRuleVariableT(rule.type),
          matching_rule: contentRuleRuleToMatchingOperator(rule.rule),
          match: contentRuleMatchToMatchingRule(rule.match),
        };
      }),
    },
  };
};

const convertContentToBranchNodes = (nodes: FlowEmailStateT['nodes']) => {
  if (nodes === null) return nodes;
  const labelFix: Record<string, string> = {};
  return nodes
    .map((node) => {
      if (node.nodeType === FlowNodeTypes.CONTENT) {
        if (typeof node.payload.non_match_node_id === 'string') {
          labelFix[node.payload.non_match_node_id] = 'No match';
        }
        return convertContentToBranchNode(node);
      }
      return node;
    })
    .map((node) => {
      if (labelFix[node.id]) {
        return {
          ...node,
          connectionLabel: labelFix[node.id],
          connectionLabelId: labelFix[node.id],
        };
      }
      return node;
    });
};

const getNodes = (state: RootReducerStateT) =>
  convertContentToBranchNodes(state.emailFlow.nodes);
const getFlowName = (state: RootReducerStateT) =>
  state.emailFlow.name as string;
const selectFlowData = createSelector(
  [getId, getNodes, getFlowName],
  (
    id: string | undefined,
    nodes: FlowEmailStateT['nodes'],
    name: string,
  ): FlowDataT => {
    return {
      actions: nodes ?? [],
      active: true,
      created_at: '',
      id: id ?? '',
      name,
      updated_at: '',
    };
  },
);

const mapStateToProps = (
  state: RootReducerStateT,
  ownProps: FlowEmailOwnPropsT,
) => {
  return {
    email_address: state.emailFlow.email_address as string,
    email_id: state.emailFlow.email_id,
    flowData: selectFlowData(state, ownProps),
    id: ownProps.params.flowId,
    nodes: state.emailFlow.nodes,
    status: state.emailFlow.meta.asyncStatus,
    agentDisplayName: state.settings.profile.profile.name,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => {
  return {
    fetchFlow: bindActionToPromise(dispatch, emailFlowGet),
    notificationAdd: (payload: NotificationPayloadT) =>
      dispatch(notificationAdd(payload)),
    resetState: () => dispatch(emailFlowResetState()),
    updateFlow: bindActionToPromise(dispatch, emailFlowEdit),
  };
};

export type FlowEmailStatePropsT = ReturnType<typeof mapStateToProps>;
export type FlowEmailDispatchPropsT = ReturnType<typeof mapDispatchToProps>;

export default withRouter(
  connect<
    FlowEmailStatePropsT,
    FlowEmailDispatchPropsT,
    FlowEmailOwnPropsT,
    RootReducerStateT
  >(
    mapStateToProps,
    mapDispatchToProps,
  )(PowerEmailFlow),
);
